'use client';
import { BASE_API_URL } from '@/config/environment';
import Link from 'next/link';
import * as Icon from '@/ui/Icon';
import * as Styled from './styled';
import { Simple as Tooltip } from '@/ui/Tooltip';
import MaybeEvalStatus from '../MaybeEvalStatus';

import { CalculatedColumn, RenderHeaderCellProps } from 'react-data-grid';

import { Row, Stack } from '@/ui/primatives';
import { TRenderAsEnum } from '../../DataGrid/lib';

interface ColumnWithDType extends CalculatedColumn<string> {
  dtype: string;
  metadata?: any;
  fileRootPath?: string;
  added?: boolean;
  removed?: boolean;
  modified?: boolean;
}

const getImageLocation = (field: ColumnWithDType, value: string, rootPath?: string, height?: string) => {
  try {
    const urlObject = new URL(value);
    return { linkText: urlObject.pathname, entryPath: urlObject.href, imagePath: urlObject.href };
  } catch (e) {
    const fileRootPath = `${BASE_API_URL}/repos/${rootPath ?? field.fileRootPath}`;
    const entryRootPath = `${rootPath ?? field.fileRootPath}`;
    let imagePath = new URL([fileRootPath, value].join('/'));
    if (height) {
      imagePath.searchParams.append('height', height);
    }

    const entryPath = `/${[entryRootPath, value].join('/')}`;

    const cleanValue = value.split('?')[0];
    const res = cleanValueString(cleanValue);

    return { entryPath, imagePath, linkText: cleanValue };
  }
};

const cleanValueString = (value: string) => {
  try {
    const url = new URL(value);
    url.searchParams.delete('oxen_expires');
    url.searchParams.delete('oxen_signature');
    return url.pathname;
  } catch (error) {
    return value;
  }
};

export const Details: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { field: ColumnWithDType; value: string; rootPath?: string }
> = ({ field, value, rootPath, ...rest }) => {
  if (!value) {
    return <span className="dim">NO IMAGE AVAILABLE</span>;
  }
  const { entryPath, imagePath, linkText } = getImageLocation(field, value, rootPath, '256');

  return (
    <Styled.ImageDetails {...rest}>
      <MaybeEvalStatus value={value}>
        <Stack gap={10}>
          <Styled.ImageContainer>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img height="256px" alt={linkText} src={`${imagePath}`} />
          </Styled.ImageContainer>
          <Link href={entryPath} className="link">
            <span>{linkText}</span>
          </Link>
        </Stack>
      </MaybeEvalStatus>
    </Styled.ImageDetails>
  );
};

export const Cell: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { field: ColumnWithDType; value: string; renderAs: TRenderAsEnum }
> = ({ field, value, renderAs, ...rest }) => {
  if (!value) {
    return (
      <Styled.ImageCell {...rest}>
        <MaybeEvalStatus value={value}>
          <Row alignItems="center" gap={20}>
            <span className="dim">NO IMAGE AVAILABLE</span>
          </Row>
        </MaybeEvalStatus>
      </Styled.ImageCell>
    );
  }
  const { entryPath, imagePath, linkText } = getImageLocation(field, value, undefined, '30');

  return (
    <Styled.ImageCell {...rest}>
      <MaybeEvalStatus value={value}>
        <Row alignItems="center" gap={5}>
          <Styled.ImageWrapper>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className="image-thumbnail" height="32px" width="32px" alt={linkText} src={`${imagePath}`} />
          </Styled.ImageWrapper>
          <Link href={entryPath} className="link">
            <span>{linkText}</span>
          </Link>
        </Row>
      </MaybeEvalStatus>
    </Styled.ImageCell>
  );
};

export const Header: React.FC<{ children: React.ReactNode } & RenderHeaderCellProps<any>> = (props) => {
  const field = props.column as ColumnWithDType;
  const renderFunction = field?.metadata?._oxen?.render;
  return (
    <Row alignItems="center" justifyContent="space-between" gap={10}>
      <span>{props.column.name}</span>
      <Row alignItems="center" gap={10}>
        {renderFunction && renderFunction.func && (
          <Tooltip content={`The content in the cell is derived from the custom "${renderFunction.func}" renderer`}>
            <Icon.QuestionCircle />
          </Tooltip>
        )}
        {props.children}
      </Row>
    </Row>
  );
};
